import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;
declare const google: any;

const DEFAULT_CENTER = { lat: 19.4326, lng: -99.1332 }; // Ciudad de México

export const MEXICAN_STATES: string[] = [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
    'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima',
    'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo',
    'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
    'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
    'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

@Component({
    selector: 'app-personal-data-tab',
    templateUrl: './personal-data-tab.component.html',
    styleUrls: ['./personal-data-tab.component.css', '../employee-tab-shared.css']
})
export class PersonalDataTabComponent implements OnInit, AfterViewInit {
    @Input() employee: any;
    @Output() saved = new EventEmitter<any>();

    @ViewChild('previewMap') previewMapEl?: ElementRef<HTMLDivElement>;
    @ViewChild('modalMap') modalMapEl?: ElementRef<HTMLDivElement>;

    form!: FormGroup;
    states = MEXICAN_STATES;
    bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
    maritalStatuses = ['Soltero/a', 'Casado/a', 'Unión libre', 'Divorciado/a', 'Viudo/a'];
    educationLevels = ['Primaria', 'Secundaria', 'Preparatoria', 'Licenciatura', 'Posgrado'];

    hasLocation = false;
    private previewMap: any;
    private previewMarker: any;
    private modalMap: any;
    private modalMarker: any;
    private draftLat: number | null = null;
    private draftLng: number | null = null;
    private draftAddressComponents: any = null;

    constructor(
        private fb: FormBuilder,
        private employeeService: EmployeeService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.hasLocation = !!(this.employee?.latitude && this.employee?.longitude);
        this.form = this.fb.group({
            street_number: [this.employee?.street_number || ''],
            neighborhood: [this.employee?.neighborhood || ''],
            zip_code: [this.employee?.zip_code || ''],
            city: [this.employee?.city || ''],
            state: [this.employee?.state || ''],
            latitude: [this.employee?.latitude ?? null],
            longitude: [this.employee?.longitude ?? null],
            email_1: [this.employee?.email_1 || '', Validators.email],
            email_2: [this.employee?.email_2 || '', Validators.email],
            phone_1: [this.employee?.phone_1 || ''],
            phone_2: [this.employee?.phone_2 || ''],
            marital_status: [this.employee?.marital_status || ''],
            blood_type: [this.employee?.blood_type || ''],
            federal_entity: [this.employee?.federal_entity || ''],
            education_level: [this.employee?.education_level || ''],
            profession: [this.employee?.profession || '']
        });
    }

    ngAfterViewInit(): void {
        this.whenGoogleMapsReady(() => this.initPreviewMap());
    }

    private markerIcon(): any {
        return {
            url: 'assets/img/icons/map-marker.svg',
            scaledSize: new google.maps.Size(28, 31),
            anchor: new google.maps.Point(14, 27)
        };
    }

    private whenGoogleMapsReady(cb: () => void, attempt = 0): void {
        if ((window as any).google?.maps) {
            cb();
            return;
        }
        if (attempt > 40) return; // ~10s, give up silently
        setTimeout(() => this.whenGoogleMapsReady(cb, attempt + 1), 250);
    }

    private initPreviewMap(): void {
        if (!this.previewMapEl?.nativeElement) return;
        const lat = this.form.value.latitude ?? DEFAULT_CENTER.lat;
        const lng = this.form.value.longitude ?? DEFAULT_CENTER.lng;
        this.previewMap = new google.maps.Map(this.previewMapEl.nativeElement, {
            center: { lat: Number(lat), lng: Number(lng) },
            zoom: this.hasLocation ? 15 : 11,
            disableDefaultUI: true,
            gestureHandling: 'none',
            zoomControl: false
        });
        if (this.hasLocation) {
            this.previewMarker = new google.maps.Marker({ position: { lat: Number(lat), lng: Number(lng) }, map: this.previewMap, icon: this.markerIcon() });
        }
    }

    openLocationModal(): void {
        this.draftLat = this.form.value.latitude ?? null;
        this.draftLng = this.form.value.longitude ?? null;
        this.draftAddressComponents = null;
        $('#employeeLocationModal').modal('show');

        this.whenGoogleMapsReady(() => {
            setTimeout(() => {
                if (!this.modalMapEl?.nativeElement) return;
                const lat = this.draftLat ?? DEFAULT_CENTER.lat;
                const lng = this.draftLng ?? DEFAULT_CENTER.lng;
                this.modalMap = new google.maps.Map(this.modalMapEl.nativeElement, {
                    center: { lat: Number(lat), lng: Number(lng) },
                    zoom: this.draftLat ? 15 : 11
                });
                this.modalMarker = new google.maps.Marker({
                    position: { lat: Number(lat), lng: Number(lng) },
                    map: this.modalMap,
                    draggable: true,
                    icon: this.markerIcon()
                });
                this.modalMarker.addListener('dragend', () => {
                    const pos = this.modalMarker.getPosition();
                    this.draftLat = pos.lat();
                    this.draftLng = pos.lng();
                });
            }, 150); // wait for modal fade-in so the map container has real dimensions
        });
    }

    onPlaceChanged(place: any): void {
        const location = place?.geometry?.location;
        if (!location) return;
        this.draftLat = typeof location.lat === 'function' ? location.lat() : location.lat;
        this.draftLng = typeof location.lng === 'function' ? location.lng() : location.lng;
        this.draftAddressComponents = place.address_components || null;

        if (this.modalMap) {
            const pos = { lat: this.draftLat, lng: this.draftLng };
            this.modalMap.setCenter(pos);
            this.modalMap.setZoom(16);
            if (this.modalMarker) {
                this.modalMarker.setPosition(pos);
            } else {
                this.modalMarker = new google.maps.Marker({ position: pos, map: this.modalMap, draggable: true, icon: this.markerIcon() });
            }
        }
    }

    confirmLocation(): void {
        if (this.draftLat == null || this.draftLng == null) {
            $('#employeeLocationModal').modal('hide');
            return;
        }
        this.form.patchValue({ latitude: this.draftLat, longitude: this.draftLng });

        if (this.draftAddressComponents) {
            const get = (type: string) => this.draftAddressComponents.find((c: any) => c.types.includes(type))?.long_name || '';
            const streetNumber = get('street_number');
            const route = get('route');
            const streetLine = [route, streetNumber].filter(Boolean).join(' ');
            if (streetLine) this.form.patchValue({ street_number: streetLine });
            const neighborhood = get('sublocality_level_1') || get('sublocality') || get('neighborhood');
            if (neighborhood) this.form.patchValue({ neighborhood });
            const zip = get('postal_code');
            if (zip) this.form.patchValue({ zip_code: zip });
            const city = get('locality') || get('administrative_area_level_2');
            if (city) this.form.patchValue({ city });
            const state = get('administrative_area_level_1');
            if (state) this.form.patchValue({ state });
        }

        this.hasLocation = true;
        $('#employeeLocationModal').modal('hide');
        this.whenGoogleMapsReady(() => this.initPreviewMap());
    }

    get f() {
        return this.form.controls;
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
            return;
        }

        const payload = this.form.value;

        this.spinner.show();
        this.employeeService.update(this.employee.id, payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'Los datos personales se guardaron correctamente', icon: 'success' });
                this.saved.emit(resp);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información', icon: 'error' });
            }
        });
    }
}
